/**
 * React Query hooks for CDEK data.
 * Ported from website-repo/src/features/checkout/hooks/use-cdek-queries.ts
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  fetchPickupPointsByBounds,
  fetchTariffs,
  type CartItemForApi,
  type PickupPoint,
  type PickupPointBounds,
  type TariffsGroup,
} from "../api/cdek.api";

export const POINTS_STALE = 6 * 60 * 60 * 1000; // 6 hours — matches backend Redis TTL
const TARIFFS_STALE = 5 * 60 * 1000;

// Snap bounds to ~1 km grid so minor viewport shifts reuse cached data.
export function snapBounds(b: PickupPointBounds): PickupPointBounds {
  const f = 100; // 2 decimal places ≈ 1.1 km
  return {
    south: Math.floor(b.south * f) / f,
    west:  Math.floor(b.west  * f) / f,
    north: Math.ceil (b.north * f) / f,
    east:  Math.ceil (b.east  * f) / f,
  };
}

export function usePickupPointsByBounds(bounds: PickupPointBounds | null, enabled = true) {
  const snapped = bounds ? snapBounds(bounds) : null;
  return useQuery<PickupPoint[], Error>({
    queryKey: [
      "cdek", "pickup-points", "bounds",
      snapped?.south, snapped?.west, snapped?.north, snapped?.east,
    ],
    queryFn: ({ signal }) => fetchPickupPointsByBounds(snapped!, signal),
    enabled: enabled && snapped !== null,
    staleTime: POINTS_STALE,
    gcTime: POINTS_STALE,
    placeholderData: keepPreviousData,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
}

export function useDeliveryTariffs(
  cityCode: number | null,
  cartFingerprint: string,
  items: CartItemForApi[],
) {
  return useQuery<TariffsGroup | null, Error>({
    queryKey: ["cdek", "tariffs", cityCode, cartFingerprint],
    queryFn: ({ signal }) => fetchTariffs(cityCode!, items, signal),
    enabled: cityCode !== null && cityCode > 0 && items.length > 0,
    staleTime: TARIFFS_STALE,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    retry: 1,
  });
}
